/**
 * สถานะการนำทางภายในหน้าจอ BOM/Routing และป้ายกำกับที่ใช้ร่วมกัน
 *
 * ใช้ query string ด้วยเหตุผลเดียวกับหน้าจอ Product Master (`products/productView.ts`):
 * `App.tsx` ประกาศ route ของทุกโมดูลจาก `DESKTOP_MODULE_ROUTES` เส้นละหนึ่ง path
 * แบบตรงตัว หน้าจอนี้จึงอยู่บน path ที่ลงทะเบียนไว้แล้วเส้นเดียว
 * (`/master-data/bom-routing`) และแตกหน้าจอย่อยด้วย query — สินค้า, โรงงาน และ
 * แท็บ BOM/Routing จึงยัง bookmark ได้ ปุ่ม back ทำงาน และเมนูซ้ายยังชี้รายการเดิม
 *
 * `plant_code` อยู่ใน URL โดยตั้งใจ: BOM เป็น plant-specific และลิงก์ที่ส่งต่อกันโดย
 * ไม่ระบุโรงงาน คือลิงก์ที่คนรับเปิดแล้วเห็นสูตรของอีกโรงงานหนึ่ง
 */

import { PRODUCT_ID_PARAM, VIEW_PARAM } from '../products/productView';
import { businessDayToIso } from '../../lib/businessTime';
import type { StatusTone } from '../StatusBadge';

export const PLANT_CODE_PARAM = 'plant_code';

/** ค่าเดียวกับ path segment ของ endpoint (`.../bom`) เพื่อไม่ให้มีชื่อที่สอง */
export const BOM_VIEW_VALUE = 'bom';
export const ROUTING_VIEW_VALUE = 'routing';

export type FormulaKind = 'bom' | 'routing';

export interface FormulaView {
  /** สตริงว่าง = ยังไม่ได้ระบุสินค้า ซึ่งเป็นสถานะที่หน้าจอต้องอธิบาย ไม่ใช่ error */
  readonly productId: string;
  /** สตริงว่าง = ยังไม่ได้เลือกโรงงาน — หน้าจอเลือกให้จากสิทธิ์ที่มีจริง */
  readonly plantCode: string;
  readonly kind: FormulaKind;
}

/**
 * แปลง query เป็นหน้าจอที่ต้องแสดง
 *
 * `view` ที่ไม่ใช่ `routing` ตกเป็น BOM เสมอ — BOM เป็นสิ่งที่คนเปิดหน้านี้มาดู
 * บ่อยที่สุด และ URL ที่พิมพ์ผิดไม่ควรจบลงที่หน้าจอว่าง
 */
export function resolveFormulaView(params: URLSearchParams): FormulaView {
  return {
    productId: params.get(PRODUCT_ID_PARAM)?.trim() ?? '',
    plantCode: params.get(PLANT_CODE_PARAM)?.trim() ?? '',
    kind: params.get(VIEW_PARAM) === ROUTING_VIEW_VALUE ? 'routing' : 'bom',
  };
}

/** query ของลิงก์ "BOM และ Routing" บนหน้ารายละเอียดสินค้า */
export function bomRoutingSearch(productId: string): string {
  return `?${PRODUCT_ID_PARAM}=${encodeURIComponent(productId)}`;
}

/** query ของแท็บ/โรงงานหนึ่งชุด — ใช้กับ `setSearchParams` ให้ URL ตรงกับที่เห็นจริง */
export function formulaSearch(view: FormulaView): string {
  const params = new URLSearchParams();
  if (view.productId !== '') {
    params.append(PRODUCT_ID_PARAM, view.productId);
  }
  if (view.plantCode !== '') {
    params.append(PLANT_CODE_PARAM, view.plantCode);
  }
  params.append(VIEW_PARAM, view.kind);
  return `?${params.toString()}`;
}

/**
 * สถานะของ header ตาม `ERD.md` — ค่าที่ไม่รู้จักคืนรหัสดิบ ไม่ใช่ "ไม่ทราบ"
 * ด้วยเหตุผลเดียวกับ `productStatusLabel()`
 */
export function revisionStatusLabel(status: string | null): string {
  switch (status) {
    case 'ACTIVE':
      return 'ใช้อยู่';
    case 'OBSOLETE':
      return 'ถูกแทนที่แล้ว';
    case 'DRAFT':
      return 'ร่าง';
    case null:
      return 'ไม่ระบุสถานะ';
    default:
      return status;
  }
}

export function revisionStatusTone(status: string | null): StatusTone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'OBSOLETE':
      return 'neutral';
    case 'DRAFT':
      return 'planned';
    default:
      return 'neutral';
  }
}

/**
 * revision นี้คือฉบับที่ใช้อยู่จริงหรือไม่
 *
 * เงื่อนไขมาจาก contract ตรง ๆ และต้องครบทั้งสองข้อ: `status = ACTIVE` **และ**
 * `valid_to IS NULL` — header ที่ `ACTIVE` แต่ถูกปิดวันหมดอายุไว้แล้ว ไม่ใช่สูตรที่
 * โรงงานกำลังผลิตตาม และการอ่านแค่ `status` จะทำให้หน้าจอชี้ผิดฉบับ
 */
export function isInForce(revision: {
  readonly status: string | null;
  readonly valid_to: string | null;
}): boolean {
  return revision.status === 'ACTIVE' && revision.valid_to === null;
}

/**
 * แปลงวันที่จากช่องกรอก (`YYYY-MM-DD`) เป็น `effective_date` ตาม contract
 * (`2026-10-01T00:00:00+07:00`)
 *
 * offset มาจาก `lib/businessTime.ts` ที่เป็นที่เดียวของระบบสำหรับเขตเวลาเชิงธุรกิจ (D-055)
 * — เคยมีสองที่ที่ทำเรื่องนี้ด้วยกฎตรงข้ามกัน จึงรวมมาไว้ที่เดียว
 *
 * คืน `null` เมื่อค่าที่ได้ไม่ใช่วันที่ — ผู้เรียกต้องปฏิเสธก่อนส่ง ไม่ใช่ส่งค่าเพี้ยนออกไป
 */
export function toEffectiveDate(day: string): string | null {
  return businessDayToIso(day);
}

/** ช่วงเวลาที่ revision หนึ่งมีผล — ฉบับที่ยังไม่ถูกปิดพูดตรง ๆ ว่ายังไม่ถูกปิด */
export function validityLabel(revision: {
  readonly valid_from: string;
  readonly valid_to: string | null;
}): string {
  return revision.valid_to === null
    ? `ตั้งแต่ ${revision.valid_from} (ยังไม่ถูกแทนที่)`
    : `ตั้งแต่ ${revision.valid_from} ถึง ${revision.valid_to}`;
}
